import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { formatPortfolioPrice, getPublishedPortfolio, type PublicPortfolioItem } from '../lib/content-api';
import { resolveStaticImage } from '../lib/static-images';

const images = {
  hero: 'https://www.figma.com/api/mcp/asset/b235222a-5d7c-438f-94a3-87ad14f44037',
  acrylic: 'https://www.figma.com/api/mcp/asset/64467147-f964-4c27-8dbe-2b125abc349d',
  gelx: 'https://www.figma.com/api/mcp/asset/496c3a5c-f871-45d7-ae86-660622897151',
  pedicure: 'https://www.figma.com/api/mcp/asset/80e5def9-6b81-4b44-b972-7bda18e88bea',
  chrome: 'https://www.figma.com/api/mcp/asset/9567f07b-64ed-48e5-b039-f085685fba7a',
  french: 'https://www.figma.com/api/mcp/asset/5a46e768-10dd-43cc-9144-6aa6830e0007',
  gem: 'https://www.figma.com/api/mcp/asset/c65c729c-f507-465d-869f-c0a7765465da',
  artist: 'https://www.figma.com/api/mcp/asset/d3d13600-3c78-4c07-a281-63fa63a35049',
};

const services = [
  {
    title: 'Sculpted acrylics',
    price: 'From ₦12,000',
    duration: '1 hour',
    description: 'Custom length and shape, careful structure and a polished finish tailored to your hands.',
    image: images.acrylic,
  },
  {
    title: 'Gel-X sets',
    price: 'From ₦12,000',
    duration: '1 hour',
    description: 'Lightweight full-cover extensions with refined shaping and a clean, durable finish.',
    image: images.gelx,
  },
  {
    title: 'Luxury pedicure',
    price: 'From ₦8,000',
    duration: '1 hour',
    description: 'Detailed foot care, precise polish application and a calm appointment-only experience.',
    image: images.pedicure,
  },
];

const fallbackPortfolio: PublicPortfolioItem[] = [
  { id: 'home-1', title: 'Champagne Chrome', slug: 'champagne-chrome', category: 'Gel-X', caption: 'Almond • Medium • Chrome finish', alt_text: 'Champagne chrome almond nail design', image_path: null, image_url: images.chrome, starting_price: 18000, price_label: 'From ₦18,000', shape: 'Almond', length: 'Medium', finish: 'Chrome finish', status: 'published', is_featured: true, sort_order: 10 },
  { id: 'home-2', title: 'Soft Sculpted French', slug: 'soft-sculpted-french', category: 'Acrylic', caption: 'Coffin • Long • French detail', alt_text: 'Soft sculpted French coffin nail design', image_path: null, image_url: images.french, starting_price: 20000, price_label: 'From ₦20,000', shape: 'Coffin', length: 'Long', finish: 'French detail', status: 'published', is_featured: true, sort_order: 20 },
  { id: 'home-3', title: 'Editorial Gem Set', slug: 'editorial-gem-set', category: 'Nail art', caption: 'Square • Medium • Gem detail', alt_text: 'Editorial square nail set with gem detail', image_path: null, image_url: images.gem, starting_price: 22000, price_label: 'From ₦22,000', shape: 'Square', length: 'Medium', finish: 'Gem detail', status: 'published', is_featured: true, sort_order: 30 },
];

const faqs = [
  {
    question: 'Is a deposit required?',
    answer: 'Yes. Your appointment is confirmed only after the stated deposit is received and verified.',
  },
  {
    question: 'How should I prepare for my appointment?',
    answer: 'Arrive with clean hands or feet and share removal needs, allergies and design inspiration before the appointment.',
  },
  {
    question: 'Can I bring an inspiration photo?',
    answer: 'Yes. Inspiration is welcome and will be adapted to suit your preferred length, shape and natural nail condition.',
  },
  {
    question: 'What happens if I am late?',
    answer: 'Late arrival may reduce the available service time or require rescheduling so later appointments are not affected.',
  },
];

function Header() {
  const [open, setOpen] = useState(false);
  return (
    <header className="site-header">
      <Link className="brand" to="/" aria-label="MF Nailedit home">
        <strong>MF_NAILEDIT</strong>
        <span>NAIL ARTIST</span>
      </Link>
      <button className="menu-button" type="button" aria-expanded={open} aria-controls="mobile-menu" onClick={() => setOpen((value) => !value)}>
        {open ? 'Close' : 'Menu'}
      </button>
      <nav className="desktop-nav" aria-label="Primary navigation">
        <Link className="active" to="/">Home</Link>
        <Link to="/services">Services</Link>
        <Link to="/portfolio">Portfolio</Link>
        <Link to="/about">About</Link>
        <Link to="/book/service">Booking</Link>
        <Link to="/contact">Contact</Link>
      </nav>
      <Link className="button button-primary header-cta" to="/book/service">Book appointment</Link>
      {open && (
        <nav id="mobile-menu" className="mobile-nav" aria-label="Mobile navigation">
          <Link to="/" onClick={() => setOpen(false)}>Home</Link>
          <Link to="/services" onClick={() => setOpen(false)}>Services</Link>
          <Link to="/portfolio" onClick={() => setOpen(false)}>Portfolio</Link>
          <Link to="/about" onClick={() => setOpen(false)}>About</Link>
          <Link to="/book/service" onClick={() => setOpen(false)}>Booking</Link>
          <Link to="/contact" onClick={() => setOpen(false)}>Contact</Link>
        </nav>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="site-footer">
      <div className="footer-brand">
        <h2>MF_NAILEDIT</h2>
        <p>Luxury nail artistry and pedicure appointments in Akure, Ondo State.</p>
        <p className="accent-text">@mf_nailedit • WhatsApp booking support</p>
      </div>
      <div className="footer-links">
        <div><h3>EXPLORE</h3><Link to="/services">Services</Link><Link to="/portfolio">Portfolio</Link><Link to="/about">About</Link></div>
        <div><h3>BOOKING</h3><Link to="/book/service">Book appointment</Link><a href="#policies">Policies</a><Link to="/contact">Contact</Link></div>
        <div><h3>STUDIO</h3><span>Akure, Ondo State</span><span>Appointment only</span><a href="https://instagram.com/mf_nailedit" rel="noreferrer" target="_blank">Instagram</a></div>
      </div>
    </footer>
  );
}

export default function Home() {
  const [openFaq, setOpenFaq] = useState(0);
  const [featuredPortfolio, setFeaturedPortfolio] = useState<PublicPortfolioItem[]>(fallbackPortfolio);

  useEffect(() => {
    const controller = new AbortController();
    getPublishedPortfolio(controller.signal)
      .then((items) => setFeaturedPortfolio(items.filter((item) => item.is_featured).slice(0, 3)))
      .catch(() => setFeaturedPortfolio(fallbackPortfolio));
    return () => controller.abort();
  }, []);

  return (
    <div className="home-page">
      <Header />

      <main>
        <section className="hero section-shell">
          <div className="hero-copy">
            <p className="eyebrow">AKURE • ONDO STATE</p>
            <h1>Nail artistry, finished like couture.</h1>
            <p className="lead">A private nail and pedicure experience built around precision, healthy care and unmistakable personal style.</p>
            <div className="hero-actions">
              <Link className="button button-primary" to="/book/service">Book your appointment</Link>
              <Link className="button button-secondary" to="/portfolio">Explore our work</Link>
            </div>
          </div>
          <div className="hero-image frame-image"><img src={resolveStaticImage(images.hero)} alt="MF_NAILEDIT luxury nail studio brand artwork" loading="eager" decoding="async" fetchPriority="high" /></div>
        </section>

        <section className="trust-strip" aria-label="Studio standards">
          <span>APPOINTMENT ONLY</span><span>SANITISED TOOLS</span><span>PREMIUM PRODUCTS</span><span>DEPOSIT SECURES SLOT</span>
        </section>

        <section className="content-section surface-section">
          <div className="section-shell stack-gap">
            <div className="section-heading">
              <p className="eyebrow">SIGNATURE SERVICES</p>
              <h2>Care designed around your style</h2>
              <p className="lead">Three client favourites, with transparent starting prices and one-hour appointments.</p>
            </div>
            <div className="card-grid services-grid">
              {services.map((service) => (
                <article className="service-card" key={service.title}>
                  <img src={resolveStaticImage(service.image)} alt={`${service.title} by MF_NAILEDIT`} loading="lazy" decoding="async" />
                  <h3>{service.title}</h3>
                  <div className="price-row"><strong>{service.price}</strong><span>{service.duration}</span></div>
                  <p>{service.description}</p>
                  <Link className="button button-compact" to="/book/service">Book service</Link>
                </article>
              ))}
            </div>
          </div>
        </section>

        {featuredPortfolio.length > 0 && <section className="content-section inverse-section">
          <div className="section-shell stack-gap">
            <div className="section-heading inverse-heading">
              <p className="eyebrow">SELECTED WORK</p>
              <h2>Looks worth saving — and booking.</h2>
              <p className="lead">Each featured look can begin the appointment request with the inspiration already selected.</p>
            </div>
            <div className="card-grid portfolio-grid">
              {featuredPortfolio.map((item) => (
                <article className="portfolio-card" key={item.id}>
                  <img src={resolveStaticImage(item.image_url)} alt={item.alt_text || item.title} loading="lazy" decoding="async" />
                  <h3>{item.title}</h3>
                  <p>{item.caption}</p>
                  <strong>{formatPortfolioPrice(item)}</strong>
                  <Link className="button button-compact" to={`/book/service?look=${encodeURIComponent(item.slug)}`}>Book this look</Link>
                </article>
              ))}
            </div>
          </div>
        </section>}

        <section className="artist-section content-section">
          <div className="section-shell split-layout">
            <div className="artist-image frame-image"><img src={resolveStaticImage(images.artist)} alt="MF_NAILEDIT nail studio" loading="lazy" decoding="async" /></div>
            <div className="artist-copy">
              <p className="eyebrow">THE ARTIST BEHIND THE DETAIL</p>
              <h2>Every appointment should feel personal — never rushed, never copied.</h2>
              <p className="lead">MF_NAILEDIT combines disciplined preparation, refined technique and creative direction so every set feels considered from first consultation to final top coat.</p>
              <Link className="button button-secondary" to="/about">Meet the artist</Link>
            </div>
          </div>
        </section>

        <section className="content-section surface-section">
          <div className="section-shell stack-gap">
            <div className="section-heading">
              <p className="eyebrow">CLIENT EXPERIENCE</p>
              <h2>Luxury is built into the way the appointment is handled.</h2>
              <p className="lead">These are service standards the studio controls—not unverified customer quotations.</p>
            </div>
            <div className="experience-grid">
              <article className="experience-card"><span>01</span><h3>Private, unrushed appointments.</h3><p>Time is reserved for preparation, shaping, detail and aftercare without unnecessary rushing.</p><small>SERVICE STANDARD • NOT A REVIEW</small></article>
              <article className="experience-card featured"><span>02</span><h3>Clear service from beginning to end.</h3><p>Pricing guidance, hygiene standards and deposit expectations are explained before the appointment.</p><small>SERVICE STANDARD • NOT A REVIEW</small></article>
            </div>
          </div>
        </section>

        <section id="policies" className="content-section faq-section">
          <div className="section-shell faq-layout">
            <div className="section-heading faq-heading">
              <p className="eyebrow">BEFORE YOU BOOK</p>
              <h2>Clear policies create a better appointment.</h2>
              <p className="lead">The most important questions are answered before checkout.</p>
            </div>
            <div className="faq-list">
              {faqs.map((faq, index) => {
                const open = openFaq === index;
                return (
                  <article className="faq-item" key={faq.question}>
                    <button type="button" aria-expanded={open} onClick={() => setOpenFaq(open ? -1 : index)}>
                      <span>{faq.question}</span><span>{open ? '−' : '+'}</span>
                    </button>
                    {open && <p>{faq.answer}</p>}
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="final-cta-section">
          <div className="booking-cta">
            <h2>Ready for your next signature set?</h2>
            <p>Choose your service, request a day and share your inspiration. Studio Admin assigns the exact one-hour appointment time.</p>
            <Link className="button button-primary" to="/book/service">Start booking</Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
